import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SafeHTML from '../components/SafeHTML';
import { getFaqKey } from '../utils/keys';

const CyprusVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: t('cyFaq1Q'),
      answer: t('cyFaq1A')
    },
    {
      question: t('cyFaq2Q'),
      answer: t('cyFaq2A')
    },
    {
      question: t('cyFaq3Q'),
      answer: t('cyFaq3A')
    },
    {
      question: t('cyFaq4Q'),
      answer: t('cyFaq4A')
    },
    {
      question: t('cyFaq5Q'),
      answer: t('cyFaq5A')
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section 
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1580837119756-563d608dd119?w=1920&q=80)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4">
              {t('cyBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('cyHeroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8">
              {t('cyHeroSubtitle')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20a%20Cyprus%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
            >
              {t('cyHeroBtn')}
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        <div className="prose prose-lg max-w-none mb-16">
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed" html={t('cyIntro1')} />
          <SafeHTML tag="p" className="text-gray-700 text-lg leading-relaxed mt-4" html={t('cyIntro2')} />
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('cyBestPlacesTitle')}</h2>
          <div className="space-y-6">
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('cyPlace1Name')}</h3>
              <p className="text-gray-700">{t('cyPlace1Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('cyPlace2Name')}</h3>
              <p className="text-gray-700">{t('cyPlace2Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('cyPlace3Name')}</h3>
              <p className="text-gray-700">{t('cyPlace3Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('cyPlace4Name')}</h3>
              <p className="text-gray-700">{t('cyPlace4Desc')}</p>
            </div>
            <div className="p-6 bg-blue-50 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('cyPlace5Name')}</h3>
              <p className="text-gray-700">{t('cyPlace5Desc')}</p>
            </div>
          </div>
        </div>

        <div className="mb-16 bg-gradient-to-r from-logo-blue to-logo-cyan p-8 rounded-lg text-white">
          <h2 className="text-3xl font-bold mb-4">{t('cyWhyTravelTitle')}</h2>
          <ul className="space-y-3 text-lg">
            <li><SafeHTML tag="span" html={'• ' + t('cyWhyReason1')} /></li>
            <li><SafeHTML tag="span" html={'• ' + t('cyWhyReason2')} /></li>
            <li><SafeHTML tag="span" html={'• ' + t('cyWhyReason3')} /></li>
            <li><SafeHTML tag="span" html={'• ' + t('cyWhyReason4')} /></li>
          </ul>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('cyVisaAppTitle')}</h2>
          <SafeHTML tag="p" className="text-gray-700 text-lg mb-6" html={t('cyVisaAppDesc')} />
          <a
            href="https://wa.me/971585862103?text=I%20want%20Cyprus%20Visa"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
          >
            {t('cyVisaAppBtn')}
          </a>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('cyFaqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default CyprusVisaPage;
